import React, { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useVideoInterview, useUploadVideo } from '../hooks/videoInterviewHook';
import { useRecaptcha } from '@/modules/shared/hooks/use-recaptcha';
import { ArrowLeft, Lock } from 'lucide-react';
import VideoRecorder from './components/VideoRecorder';
import QuestionList from './components/QuestionList';

export default function VideoInterviewPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { data, isLoading, error } = useVideoInterview(token);
    const uploadVideo = useUploadVideo(token || '');
    const { getToken: getCaptchaToken } = useRecaptcha();

    const handleUpload = useCallback(async (blob: Blob) => {
        try {
            const captchaToken = await getCaptchaToken("interview_upload_video");
            await uploadVideo.mutateAsync({ videoBlob: blob, captchaToken });
            navigate('/video-interview/success');
        } catch (err: unknown) {
            console.error("Upload failed", err);
            const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
            navigate(`/video-interview/failure?returnUrl=/video-interview/${token}/record&error=${encodeURIComponent(errorMessage)}`);
        }
    }, [uploadVideo, navigate, token, getCaptchaToken]);

    if (!token) return null;

    if (isLoading) {
        return (
            <div className="h-screen w-screen flex items-center justify-center bg-black text-white">
                <p>Loading interview environment...</p>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="h-screen w-screen flex items-center justify-center bg-black text-white">
                <p>{error instanceof Error ? error.message : "Error loading interview. Please refresh."}</p>
            </div>
        );
    }

    if (data.alreadySubmitted) {
        return (
            <div className="min-h-screen bg-muted flex flex-col items-center justify-center p-4">
                <div className="max-w-md w-full bg-card rounded-2xl shadow-xl overflow-hidden">
                    <div className="flex flex-col items-center text-center py-16 px-8 gap-4">
                        <div className="rounded-full bg-slate-100 p-5">
                            <Lock className="w-14 h-14 text-slate-500" />
                        </div>
                        <h1 className="text-3xl font-bold text-[#1b237a]">
                            Video already submitted
                        </h1>
                        <p className="text-muted-foreground max-w-sm">
                            A video interview for {data.vacancyTitle}
                            {data.organizationName && <> at {data.organizationName}</>}
                            {" "}has already been uploaded for this candidate. This link can no longer be used to record.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen w-screen flex flex-col bg-muted overflow-hidden">
            {/* Header - Minimal */}
            <div className="h-16 flex items-center px-6 bg-card border-b border-border shrink-0 justify-between shadow-sm z-10">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground transition-colors p-1 hover:bg-muted rounded-full">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-foreground font-semibold text-lg leading-tight">{data.vacancyTitle}</h1>
                        <p className="text-muted-foreground text-xs">{data.organizationName}</p>
                    </div>
                </div>
                <div className="text-muted-foreground text-sm font-medium bg-muted px-3 py-1 rounded-full">
                    Candidate: {data.candidateName}
                </div>
            </div>

            {/* Main Content - Split Layout */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left Side - Recorder (2/3) */}
                <div className="w-2/3 p-6 flex flex-col justify-center">
                    <div className="flex-1 bg-card rounded-2xl overflow-hidden shadow-xl border border-border ring-4 ring-border">
                        <VideoRecorder onUpload={handleUpload} isUploading={uploadVideo.isPending} />
                    </div>
                </div>

                {/* Right Side - Questions (1/3) */}
                <div className="w-1/3 bg-card border-l border-border shadow-xl z-10 flex flex-col">
                    <QuestionList questions={data.questions} />
                </div>
            </div>
        </div>
    );
}
